import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';

import {
  FiatWalletAccountState,
  FiatWalletKycState,
} from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { CreateFiatWallet } from '#src/components/fiatWallet/CreateFiatWallet/index.tsx';
import { SettingsSection } from '#src/components/settings/SettingsSection/index.tsx';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useToggle } from '#src/hooks/useToggle.ts';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';

import { CreateOAuthApplicationDialog } from './CreateOAuthApplicationDialog/index.tsx';
import { OAuthApplicationRow } from './OAuthApplicationRow/index.tsx';
import type {
  CreateOAuthApplicationMutation,
  CreateOAuthApplicationMutationVariables,
  OAuthApplicationsManagementQuery,
  OAuthApplicationsManagementQueryVariables,
} from './__generated__/index.graphql.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'OAuth applications',
  },
  description: {
    defaultMessage:
      'Build “Login with Sorare” into your app. Requires a verified identity.',
  },
  noApplications: {
    defaultMessage: 'You haven’t requested any OAuth application yet.',
  },
  request: {
    defaultMessage: 'Request an application',
  },
  kycRequired: {
    defaultMessage: 'Verify your identity to create OAuth applications.',
  },
  verifyAccount: {
    defaultMessage: 'Verify my account',
  },
});

const OAUTH_APPLICATIONS_MANAGEMENT_QUERY = gql`
  query OAuthApplicationsManagementQuery {
    currentUser {
      id
      slug
      ownedOAuthApplications {
        id
        ...OAuthApplicationRow_oAuthApplication
      }
    }
  }
  ${OAuthApplicationRow.fragments.oAuthApplication}
` as TypedDocumentNode<
  OAuthApplicationsManagementQuery,
  OAuthApplicationsManagementQueryVariables
>;

const CREATE_OAUTH_APPLICATION_MUTATION = gql`
  mutation CreateOAuthApplicationMutation(
    $input: createOAuthApplicationInput!
  ) {
    createOAuthApplication(input: $input) {
      oAuthApplication {
        id
        ...OAuthApplicationRow_oAuthApplication
      }
      currentUser {
        id
        slug
        ownedOAuthApplications {
          id
          ...OAuthApplicationRow_oAuthApplication
        }
      }
      errors {
        message
      }
    }
  }
  ${OAuthApplicationRow.fragments.oAuthApplication}
` as TypedDocumentNode<
  CreateOAuthApplicationMutation,
  CreateOAuthApplicationMutationVariables
>;

export const OAuthApplicationsManagement = () => {
  const { data, loading } = useQuery(OAUTH_APPLICATIONS_MANAGEMENT_QUERY);
  const [createApplication] = useMutation(CREATE_OAUTH_APPLICATION_MUTATION);
  const [showDialog, toggleDialog] = useToggle(false);
  const [showVerification, toggleVerification] = useToggle(false);
  const { kycStatus } = useFiatBalance();

  const applications = data?.currentUser?.ownedOAuthApplications;
  const kycValidated = kycStatus === FiatWalletKycState.VALIDATED;

  return (
    <SettingsSection title={messages.title} description={messages.description}>
      {loading ? (
        <LoadingIndicator />
      ) : (
        <Vertical gap={2}>
          {applications?.length ? (
            <Vertical gap={0}>
              {applications.map(application => (
                <OAuthApplicationRow
                  key={application.id}
                  application={application}
                />
              ))}
            </Vertical>
          ) : kycValidated ? (
            <BodyM color="var(--c-nd-600)">
              <FormattedMessage {...messages.noApplications} />
            </BodyM>
          ) : null}
          {kycValidated ? (
            <div>
              <Button
                size="medium"
                color="tertiary"
                onClick={() => toggleDialog()}
              >
                <FormattedMessage {...messages.request} />
              </Button>
            </div>
          ) : (
            <Vertical gap={1}>
              <BodyM color="var(--c-nd-600)">
                <FormattedMessage {...messages.kycRequired} />
              </BodyM>
              <div>
                <Button
                  size="medium"
                  color="primary"
                  onClick={() => toggleVerification()}
                >
                  <FormattedMessage {...messages.verifyAccount} />
                </Button>
              </div>
            </Vertical>
          )}
        </Vertical>
      )}
      <CreateOAuthApplicationDialog
        open={!!kycValidated && showDialog}
        onClose={() => toggleDialog()}
        createApplication={createApplication}
      />
      {!!showVerification && (
        <CreateFiatWallet
          statusTarget={FiatWalletAccountState.VALIDATED_OWNER}
          onClose={() => toggleVerification()}
          onDismissActivationSuccess={() => toggleVerification()}
          canDismissAfterActivation
        />
      )}
    </SettingsSection>
  );
};
